import { useEffect, useState } from 'react';
import { m, AnimatePresence } from 'framer-motion';
import { GameState, Player, PlayerState } from '@brainfart/shared';
import { ConfettiBurst } from './Confetti';
import { RESULTS_LOADING_TEXTS } from '../flavorText';
import { multiplayerAwards } from '../superlatives';
import { trackShareClicked } from '../telemetry';

const SITE_URL = 'https://brainfart.prasadkhake.com';

interface LeaderboardScreenProps {
  game: GameState;
  me: Player;
  final?: boolean;
  canStartNext?: boolean;
  hostName?: string;
  onPlayAgain: () => void;
  onLeave?: () => void;
}

// Medals and the winner banner already cover the top spots; the only extra
// title is the wooden spoon (quiz slang for last place) in a bigger game.
function lastPlaceTitle(idx: number, total: number): string | null {
  return total > 4 && idx === total - 1 ? 'Wooden Spoon' : null;
}

function ResultsLoadingScreen() {
  const [textIdx, setTextIdx] = useState(0);
  useEffect(() => {
    const interval = setInterval(() => setTextIdx(i => (i + 1) % RESULTS_LOADING_TEXTS.length), 700);
    return () => clearInterval(interval);
  }, []);
  return (
    <m.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '1.5rem' }}
    >
      <m.div
        animate={{ rotate: 360 }}
        transition={{ duration: 1.2, repeat: Infinity, ease: 'linear' }}
        style={{ fontSize: '3.5rem' }}
      >
        🏆
      </m.div>
      <AnimatePresence mode="wait">
        <m.p
          key={textIdx}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          style={{ color: '#FFC93C', fontWeight: 700, fontSize: '1.1rem' }}
        >
          {RESULTS_LOADING_TEXTS[textIdx]}
        </m.p>
      </AnimatePresence>
    </m.div>
  );
}

export function LeaderboardScreen({ game, me, final = false, canStartNext = false, hostName, onPlayAgain, onLeave }: LeaderboardScreenProps) {
  const [celebrate, setCelebrate] = useState(false);
  const [shareCopied, setShareCopied] = useState(false);
  const [showLoading, setShowLoading] = useState(final);

  useEffect(() => {
    const timer = setTimeout(() => setCelebrate(true), 800);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (!final) return;
    const timer = setTimeout(() => setShowLoading(false), 2600);
    return () => clearTimeout(timer);
  }, [final]);

  const sortedPlayers = [...game.players].sort((a: PlayerState, b: PlayerState) => b.score - a.score);
  const winner = sortedPlayers[0];

  // Per-player "awards" (Fastest Finger, Sharpshooter, Confidently Wrong...).
  const awardsByPlayer = multiplayerAwards(game);

  if (final && showLoading) {
    return <ResultsLoadingScreen />;
  }

  return (
    <m.div
      className="leaderboard-screen"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        background: final ? 'radial-gradient(ellipse at center, #241C10 0%, #0F0D1F 70%)' : '#0F0D1F',
        overflow: 'hidden',
      }}
    >
      {/* Confetti */}
      {final && celebrate && <ConfettiBurst />}

      {/* Header */}
      <header style={{ padding: '1.25rem 1.5rem', display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: '0.5rem', borderBottom: '1px solid #2E2B45', position: 'relative', zIndex: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', minWidth: 0 }}>
          <m.span
            animate={{ rotate: final ? [0, 10, -10, 0] : 0 }}
            transition={{ duration: 2, repeat: final ? Infinity : 0, repeatDelay: 3 }}
            style={{ fontSize: '1.75rem', flexShrink: 0 }}
          >
            {final ? '🏁' : '📊'}
          </m.span>
          <h1 style={{ fontSize: 'clamp(1.1rem, 5vw, 1.5rem)', fontWeight: 700, background: 'linear-gradient(135deg, #FFC93C, #FF9F1C)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', whiteSpace: 'nowrap' }}>
            {final ? 'Final Results' : 'Leaderboard'}
          </h1>
        </div>
        <div style={{ color: '#666', fontSize: '0.8rem' }}>
          {game.players.length} players • {game.settings.rounds} rounds
        </div>
      </header>

      <main style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '2rem', position: 'relative', zIndex: 5 }}>
        {/* Winner Announcement */}
        {final && winner.id !== me.id && (
          <m.div
            initial={{ opacity: 0, y: -30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            style={{
              textAlign: 'center',
              marginBottom: '2rem',
              padding: '1.5rem 2rem',
              background: 'rgba(255,201,60,0.1)',
              border: '1px solid rgba(255,201,60,0.3)',
              borderRadius: '16px',
              maxWidth: '500px',
            }}
          >
            <m.div animate={{ y: [0, -8, 0] }} transition={{ duration: 2, repeat: Infinity }} style={{ fontSize: '3rem', lineHeight: 1, marginBottom: '0.5rem' }}>👑</m.div>
            <p style={{ color: '#FFC93C', fontSize: '0.85rem', letterSpacing: '0.15em', marginBottom: '0.25rem' }}>WINNER</p>
            <h2 style={{ fontSize: 'clamp(1.5rem, 6vw, 2rem)', fontWeight: 700, background: 'linear-gradient(135deg, #FFC93C, #FF9F1C)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
              {winner.name}
            </h2>
          </m.div>
        )}

        {final && winner.id === me.id && (
          <m.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.4, type: 'spring', stiffness: 80, damping: 12 }}
            style={{
              textAlign: 'center',
              marginBottom: '2rem',
              padding: '2rem',
              background: 'linear-gradient(135deg, rgba(255,201,60,0.15), rgba(255,159,28,0.1))',
              border: '2px solid #FFC93C',
              borderRadius: '20px',
              maxWidth: '500px',
            }}
          >
            <m.div animate={{ y: [0, -8, 0] }} transition={{ duration: 2, repeat: Infinity }} style={{ fontSize: '3rem', lineHeight: 1 }}>👑</m.div>
            <h2 style={{ marginTop: '1rem', fontSize: 'clamp(1.5rem, 6vw, 2rem)', fontWeight: 700, background: 'linear-gradient(135deg, #FFC93C, #FF9F1C)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
              YOU WON!
            </h2>
          </m.div>
        )}

        {/* Leaderboard Table */}
        <m.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: final ? 0.6 : 0.2 }}
          style={{ width: '100%', maxWidth: '700px' }}
        >
          <AnimatePresence>
            {sortedPlayers.map((player, idx) => {
              const isMe = player.id === me.id;
              const isWinner = final && idx === 0;
              const medal = idx === 0 ? '🥇' : idx === 1 ? '🥈' : idx === 2 ? '🥉' : `${idx + 1}.`;

              return (
                <m.div
                  key={player.id}
                  initial={{ opacity: 0, x: -50, scale: 0.9 }}
                  animate={{ opacity: 1, x: 0, scale: 1 }}
                  exit={{ opacity: 0, x: 50, scale: 0.9 }}
                  transition={{ delay: 0.7 + idx * 0.08, type: 'spring', stiffness: 100, damping: 20 }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'clamp(0.5rem, 2vw, 1rem)',
                    padding: isMe ? '1rem' : '0.85rem 1rem',
                    marginBottom: '0.75rem',
                    background: isWinner
                      ? 'linear-gradient(135deg, rgba(255,201,60,0.15), rgba(255,159,28,0.1))'
                      : isMe
                      ? 'linear-gradient(135deg, rgba(57,255,136,0.1), rgba(57,255,136,0.05))'
                      : '#1B1830',
                    border: isWinner
                      ? '2px solid #FFC93C'
                      : isMe
                      ? '2px solid #39FF88'
                      : '1px solid #2E2B45',
                    borderRadius: '16px',
                    position: 'relative',
                    overflow: 'hidden',
                  }}
                >
                  {/* Rank */}
                  <div style={{
                    width: 'clamp(32px, 8vw, 56px)',
                    flexShrink: 0,
                    textAlign: 'center',
                    fontSize: 'clamp(1.1rem, 4vw, 1.5rem)',
                    fontWeight: 700,
                    fontFamily: 'Space Grotesk',
                    color: idx < 3 ? '#FFC93C' : '#666',
                  }}>
                    {medal}
                  </div>

                  {/* Avatar */}
                  <m.div
                    animate={{ scale: isMe ? [1, 1.1, 1] : 1 }}
                    transition={{ duration: 2, repeat: isMe ? Infinity : 0, repeatDelay: 2 }}
                    style={{
                      width: 'clamp(38px, 10vw, 56px)',
                      height: 'clamp(38px, 10vw, 56px)',
                      borderRadius: '50%',
                      background: `linear-gradient(135deg, ${player.color}33, ${player.color}11)`,
                      border: `3px solid ${isWinner ? '#FFC93C' : (isMe ? '#39FF88' : player.color)}`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 'clamp(1.25rem, 5vw, 2rem)',
                      flexShrink: 0,
                      boxShadow: isWinner ? '0 0 30px rgba(255,201,60,0.4)' : isMe ? '0 0 20px rgba(57,255,136,0.3)' : 'none',
                    }}
                  >
                    {player.avatar}
                  </m.div>

                  {/* Name & Title */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap', marginBottom: final && lastPlaceTitle(idx, sortedPlayers.length) ? '0.15rem' : 0 }}>
                      <h3 style={{
                        fontSize: 'clamp(0.95rem, 4vw, 1.1rem)',
                        fontWeight: 700,
                        color: isWinner ? '#FFC93C' : (isMe ? '#39FF88' : '#fff'),
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        maxWidth: '100%',
                      }}>
                        {player.name}
                      </h3>
                      {isMe && (
                        <m.span
                          animate={{ scale: [1, 1.1, 1] }}
                          transition={{ duration: 1.5, repeat: Infinity }}
                          style={{
                            fontSize: '0.6rem',
                            fontWeight: 700,
                            padding: '0.125rem 0.5rem',
                            borderRadius: '100px',
                            background: 'linear-gradient(135deg, #39FF88, #22CC66)',
                            color: '#000',
                            flexShrink: 0,
                          }}
                        >
                          YOU
                        </m.span>
                      )}
                    </div>
                    {final && lastPlaceTitle(idx, sortedPlayers.length) && (
                      <p style={{ color: '#FF4D6A', fontSize: 'clamp(0.85rem, 3.5vw, 1rem)', fontWeight: 700 }}>
                        {lastPlaceTitle(idx, sortedPlayers.length)} 🥄
                      </p>
                    )}
                    {final && (awardsByPlayer.get(player.id) || []).length > 0 && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.3rem', marginTop: '0.35rem' }}>
                        {(awardsByPlayer.get(player.id) || []).map(award => (
                          <span
                            key={award.label}
                            style={{
                              fontSize: '0.62rem',
                              fontWeight: 600,
                              padding: '0.15rem 0.5rem',
                              borderRadius: '100px',
                              background: 'rgba(124,92,255,0.15)',
                              border: '1px solid rgba(124,92,255,0.35)',
                              color: '#B9A6FF',
                            }}
                          >
                            {award.emoji} {award.label}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Score */}
                  <m.div
                    animate={{ scale: isMe ? [1, 1.05, 1] : 1 }}
                    transition={{ duration: 2, repeat: isMe ? Infinity : 0, repeatDelay: 2 }}
                    style={{
                      textAlign: 'right',
                      flexShrink: 0,
                    }}
                  >
                    <p style={{
                      fontSize: isWinner ? 'clamp(1.5rem, 6vw, 2.5rem)' : (isMe ? 'clamp(1.25rem, 5vw, 2rem)' : 'clamp(1.1rem, 4.5vw, 1.75rem)'),
                      fontWeight: 700,
                      fontFamily: 'Space Grotesk',
                      color: isWinner ? '#FFC93C' : (isMe ? '#39FF88' : '#fff'),
                      lineHeight: 1,
                      textShadow: isWinner ? '0 0 30px rgba(255,201,60,0.5)' : (isMe ? '0 0 20px rgba(57,255,136,0.3)' : 'none'),
                    }}>
                      {player.score.toLocaleString()}
                    </p>
                    {/* Same "x/n correct" line every mode shows next to a score. */}
                    <p style={{ fontSize: '0.75rem', color: '#888', marginTop: '0.3rem', textAlign: 'right' }}>
                      {game.rounds.filter(r => r.correctness?.[player.id]).length}/{game.rounds.length} correct
                    </p>
                  </m.div>
                </m.div>
              );
            })}
          </AnimatePresence>
        </m.div>

        {/* Footer Actions */}
        {final && (
          <m.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.2 }}
            style={{ display: 'flex', gap: '1rem', marginTop: '2rem', flexWrap: 'wrap', justifyContent: 'center' }}
          >
            {canStartNext ? (
              <button
                className="btn btn-primary"
                style={{ padding: '1rem 2rem' }}
                onClick={onPlayAgain}
              >
                🔁 Play again
              </button>
            ) : (
              <p style={{ width: '100%', textAlign: 'center', color: '#aaa' }}>
                Waiting for {hostName || 'the host'} to start the next game…
              </p>
            )}
            <button
              className="btn btn-secondary"
              style={{ padding: '1rem 2rem' }}
              onClick={() => {
                trackShareClicked(game.code, 'multiplayer');
                const summary = sortedPlayers
                  .map((p, idx) => {
                    const title = lastPlaceTitle(idx, sortedPlayers.length);
                    return `${idx + 1}. ${p.name} — ${p.score.toLocaleString()} pts${title ? ` (${title})` : ''}`;
                  })
                  .join('\n');
                navigator.clipboard.writeText(`BrainFart.fun results:\n${summary}\n\nPlay at ${SITE_URL}`);
                setShareCopied(true);
                setTimeout(() => setShareCopied(false), 2000);
              }}
            >
              {shareCopied ? '✅ Copied!' : '📤 Share results'}
            </button>
            {onLeave && (
              <button className="btn btn-ghost" style={{ padding: '1rem 2rem' }} onClick={onLeave}>
                🏠 Leave
              </button>
            )}
          </m.div>
        )}
      </main>
    </m.div>
  );
}